import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/common/Button';

// Reusable responsive plan carousel: 1 card (mobile) / 2 (tablet) / 3 (desktop),
// horizontal swipe + snap, arrows (tablet) and dots (mobile/tablet), controls
// hidden on desktop where all 3 cards are visible. Shared by Host & Partner.
export function PlansCarousel({ plans, ctaTo, testidPrefix }) {
  const scrollerRef = useRef(null);
  const cardRefs = useRef([]);
  const [active, setActive] = useState(0);

  const scrollToCard = useCallback((i) => {
    const el = cardRefs.current[i];
    const sc = scrollerRef.current;
    if (el && sc) sc.scrollTo({ left: el.offsetLeft - sc.offsetLeft, behavior: 'smooth' });
  }, []);

  const onScroll = useCallback(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    let best = 0, bestDist = Infinity;
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const d = Math.abs((el.offsetLeft - sc.offsetLeft) - sc.scrollLeft);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setActive(best);
  }, []);

  useEffect(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const last = plans.length - 1;

  return (
    <div className="relative mt-16" data-testid={`${testidPrefix}-carousel`}>
      {/* Arrows (tablet only) */}
      <button
        type="button" aria-label="Piano precedente" data-testid={`${testidPrefix}-prev`}
        onClick={() => scrollToCard(Math.max(0, active - 1))}
        disabled={active === 0}
        className="absolute -left-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paelbi-line bg-white text-paelbi-brown shadow-soft transition-all hover:bg-paelbi-bg disabled:opacity-0 sm:flex lg:hidden"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button" aria-label="Piano successivo" data-testid={`${testidPrefix}-next`}
        onClick={() => scrollToCard(Math.min(last, active + 1))}
        disabled={active === last}
        className="absolute -right-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paelbi-line bg-white text-paelbi-brown shadow-soft transition-all hover:bg-paelbi-bg disabled:opacity-0 sm:flex lg:hidden"
      >
        <ChevronRight size={20} />
      </button>

      {/* Snap carousel */}
      <div
        ref={scrollerRef}
        data-testid={`${testidPrefix}-scroller`}
        className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:overflow-x-visible"
      >
        {plans.map((p, i) => {
          const featured = !!p.badge;
          return (
            <div
              key={p.id}
              ref={(el) => (cardRefs.current[i] = el)}
              data-testid={`${testidPrefix}-${p.id}`}
              className="w-[85%] shrink-0 snap-start sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]"
            >
              <div className={`relative flex h-full flex-col rounded-card bg-white p-6 md:p-8 ${featured ? 'shadow-lift ring-2 ring-paelbi-brown' : 'border border-paelbi-line shadow-card'}`}>
                {p.badge && (
                  <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-paelbi-brown px-4 py-1 text-xs font-semibold text-white">
                    <Star size={12} /> {p.badge}
                  </span>
                )}
                <h3 className="font-display text-2xl text-paelbi-ink">{p.name}</h3>
                <div className="mt-2 flex items-end gap-1">
                  <span className="font-display text-4xl text-paelbi-ink">€{p.price}</span>
                  <span className="mb-1.5 text-sm text-paelbi-ink/60">/ mese</span>
                </div>
                <p className="mt-2 min-h-[40px] font-sans text-sm text-paelbi-ink/60">{p.tagline}</p>
                <ul className="mt-5 flex-1 space-y-2.5">
                  {p.includes.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 font-sans text-sm text-paelbi-ink/80">
                      <Check size={17} className="mt-0.5 shrink-0 text-paelbi-brown" />{f}
                    </li>
                  ))}
                </ul>
                <Button to={ctaTo(p)} variant={featured ? 'primary' : 'secondary'}
                  className="mt-7 w-full justify-center" data-testid={`${testidPrefix}-select-${p.id}`}>
                  {p.cta}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dots (mobile/tablet) */}
      <div className="mt-6 flex justify-center gap-2 lg:hidden" data-testid={`${testidPrefix}-dots`}>
        {plans.map((p, i) => (
          <button
            key={p.id} type="button" aria-label={`Vai al piano ${p.name}`}
            onClick={() => scrollToCard(i)}
            className={`h-2 rounded-full transition-all ${i === active ? 'w-6 bg-paelbi-brown' : 'w-2 bg-paelbi-accent'}`}
          />
        ))}
      </div>
    </div>
  );
}

export default PlansCarousel;
